<template>
  <div class="wrp">
    <div class="detailsWrp" v-if="plan">
      <div class="detailsTitle">
        {{ getTitle }}
      </div>
      <div class="subtitle" v-if="getSubTitle" v-html="getSubTitle"></div>
      <ChangePlanItem :plan="plan" class="planMobile" />
      <div class="greenBox">
        <div class="greenBox__item">
          <div>
            <span class="greenBox__item-value">
              $ {{ numeral(billing.planPrice / 100).format('0,0[.]00') }}
            </span>
            <span class="greenBox__item-unit"> /{{ t('mo') }} </span>
          </div>
          <div class="greenBox__item-text">{{ t('Current Price') }}</div>
        </div>
        <div class="greenBox__item">
          <span class="greenBox__item-value"
            >$ {{ numeral(plan.price / 100).format('0,0[.]00') }}</span
          >
          <span class="greenBox__item-unit"> /{{ t('mo') }} </span>
          <div class="greenBox__item-text">{{ t('New Price') }}</div>
        </div>
        <div class="greenBox__item">
          <span class="greenBox__item-value">
            $
            {{
              numeral(Math.abs(plan.price - billing.planPrice) / 100).format(
                '0,0[.]00',
              )
            }}
          </span>
          <span class="greenBox__item-unit"> /{{ t('mo') }} </span>
          <div class="greenBox__item-text">{{ t('Difference') }}</div>
        </div>
      </div>
      <div v-if="!isOnePlanLimit" class="more-than-one__wrp">
        <div v-for="addon in planLimits.addons" class="pb-12">
          <div class="mb-2">
            <b>{{ planLimitsKeyText[addon] }}</b>
          </div>
          <div class="addons-list">
            <div class="buy-addon-btn">
              <ui-button
                :disabled="billing.addons?.[addon]"
                :text="t('Buy as Add-On')"
                icon="fas fa-badge-dollar"
                @click="byAsAddonHandle(addon)"
                button-style="blue"
              ></ui-button>
            </div>
            <div class="pl-10" v-if="billing.addons?.[addon]">
              {{ t('This option was already purchased as add-on') }}
            </div>
          </div>
        </div>
      </div>
      <div class="billedImmediately">
        {{ t('Billed immediately after plan upgrade or add-on purchase.') }}
      </div>
      <div class="listLimitsTitle">{{ t('Everything you get with this upgrade:') }}</div>
      <div class="listLimits">
        <div class="pr-8 listLimits__item" v-for="limit in enabledPlanLimit">
          <ui-icon
            class="mr-8 mt-1"
            :color="$colors.green"
            size="12"
            icon="fas fa-check"
          ></ui-icon>
          <div>
            {{ planLimitsKeyText[limit] || limit
            }}<span v-if="typeof billing.limits?.[limit] === 'number'"
              >: {{ (plan.limits as any)[limit] }}</span
            >
          </div>
        </div>
      </div>
    </div>
    <div v-if="!plan">
      <div class="detailsTitle">{{ t('Wish to use this feature?') }}</div>
      <div class="subtitle">{{ t('Purchase it as add-on.') }}</div>
    </div>
    <ChangePlanItem v-if="plan" :plan="plan" class="planDesktop" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import ChangePlanItem from './change-plan-item.vue';
import { PLAN_LIMITS_LOGIC } from '@/services/constants/planLimits';
import type {
  BillingPlan,
  BillingLimits,
  BillingSubscriptionState,
} from '@/services/models';
import numeral from 'numeral';
import { useColors, useI18n, usePlanLimits } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import type { PlanLimitsProp } from '@/composables';

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'select-plan', plan: BillingPlan): void;
}>();

const props = defineProps<{
  planLimits: PlanLimitsProp;
  handlePlanSelect?: (plan: BillingPlan) => void;
  billingPlans?: BillingPlan[];
  billing?: BillingSubscriptionState;
}>();

const { t } = useI18n();
const $colors = useColors();
const { planLimitsKeyText } = usePlanLimits(undefined, false);
const store = useStore();
const billingPlans = computed<BillingPlan[]>(() => {
  // Prefer non-empty props; empty array must not block store fallback
  if (props.billingPlans?.length) return props.billingPlans;
  return store.billingPlans ?? [];
});
const billing = computed<BillingSubscriptionState>(() => {
  const fromProps = props.billing;
  if (fromProps?.limits && Object.keys(fromProps.limits).length) return fromProps;
  return store.billing;
});

const isOnePlanLimit = computed(() => typeof props.planLimits === 'string');

const getTitleNeedMore = computed<string | undefined>(() => {
  switch (props.planLimits) {
    case 'memberAdminsAmount':
    case 'memberUsersAmount':
      return t('Need more members?');
    case 'domainsAmount':
    case 'redirectDomainsAmount':
      return t('Need more domains?');
    case 'campaignsAmount':
    case 'offersAmount':
    case 'landingsAmount':
    case 'flowsAmount':
    case 'trafficSourcesAmount':
    case 'affiliateNetworksAmount':
      return t('Ran out of active elements?');
    case 'customColumnsAmount':
      return t('Need more columns?');
    case 'sharedReportsAmount':
      return t('Need more shared reports?');
    case 'customConversionsAmount':
      return t('Need more custom conversions?');
  }
});

const getSubTitleNeedMore = computed<string | undefined>(() => {
  switch (props.planLimits) {
    case 'memberAdminsAmount':
    case 'memberUsersAmount':
      return t(
        'Remove the members that are no longer required or contact our team to request more members.',
      );
    case 'domainsAmount':
    case 'redirectDomainsAmount':
      return t(
        'Delete the domains that are no longer required or contact our team to request more domains.',
      );
    case 'campaignsAmount':
    case 'offersAmount':
    case 'landingsAmount':
    case 'flowsAmount':
    case 'trafficSourcesAmount':
    case 'affiliateNetworksAmount':
      return t(
        'Archive the elements that are no longer required or contact our team to request more elements.',
      );
    case 'customColumnsAmount':
      return t(
        'Delete the columns that are no longer required or contact our team to request more columns.',
      );
    case 'sharedReportsAmount':
      return t(
        'Delete the reports that are no longer required or contact our team to request more shared reports.',
      );
    case 'customConversionsAmount':
      return t(
        'Delete the custom conversions that are no longer required or contact our team to request more conversions.',
      );
  }
});

const isInvalidTrafficPack = computed(() => {
  return (
    typeof props.planLimits !== 'string' &&
    JSON.stringify(props.planLimits?.addons) ===
      '["ivtIpIsDatacenter","ivtIpIsBlacklisted","ivtFastClicker","ivtIpWithFrequentCampaignEvents","ivtIpWithFrequentEvents"]'
  );
});

const getTitle = computed<string | undefined>(() => {
  if (isInvalidTrafficPack.value) {
    return t('Wish to identify and manage the invalid traffic in your campaigns?');
  }

  switch (props.planLimits) {
    case 'domainsAmount':
      return t('Need more tracking domains?');
    case 'redirectDomainsAmount':
      return t('Need more cloaking domains?');
    case 'campaignsAmount':
      return t('Need more campaigns?');
    case 'offersAmount':
      return t('Need more offers?');
    case 'landingsAmount':
      return t('Need more landing pages?');
    case 'flowsAmount':
      return t('Need more flows?');
    case 'trafficSourcesAmount':
      return t('Need more traffic sources?');
    case 'affiliateNetworksAmount':
      return t('Need more affiliate networks?');
    case 'customColumnsAmount':
      return t('Enrich your reports with the your own custom metrics.');
    case 'doubleMetaRedirect':
      return t(
        'Hide your landing pages and traffic source data from affiliate networks.',
      );
    case 'bulkEditCampaigns':
    case 'bulkEditOffers':
    case 'bulkEditLandings':
      return t(
        'Use bulk edit to change the parameters, domains and other settings of elements quickly.',
      );
    case 'webViewCondition':
    case 'crawlerCondition':
    case 'uniquenessPeriodCondition':
    case 'botMarkCondition':
    case 'referrerDomainCondition':
    case 'proxyCondition':
    case 'visitCapCondition':
    case 'externalIdCondition':
      return t(
        'Use advanced rules for the smart traffic distribution in your campaigns.',
      );
    case 'importOffers':
      return t('Optimize the routine - add multiple offers with one click.');
    case 'importLandings':
      return t('Optimize the routine - add multiple landing pages with one click.');
    case 'bulkReportCampaign':
    case 'bulkReportOffer':
    case 'bulkReportLanding':
    case 'bulkReportFlow':
    case 'bulkReportTrafficSource':
    case 'bulkReportAffiliateNetwork':
    case 'bulkReportCustomParams':
      return t('Learn more about the traffic to optimize your campaigns.');
    case 'botFiltersAccess':
      return t('Control the bot traffic with flexible conditions and actions.');
    case 'uniquenessPeriod':
      return t('Wish to change the settings of traffic uniqueness for a campaign?');
    case 'customConversionsAmount':
      return t('Need more custom conversions?');
    case 'memberAdminsAmount':
      return t('Need more admin roles?');
    case 'memberUsersAmount':
      return t('Need more user roles?');
    case 'tagsCampaigns':
    case 'tagsOffers':
    case 'tagsAffiliateNetworks':
    case 'tagsLandings':
    case 'tagsFlows':
    case 'tagsTrafficSources':
      return t('Use tags to group and organize your work with the elements.');
    case 'trafficLossCampaign':
      return t('Wish to set up the traffic loss % on the level of a campaign?');
    case 'landingPageProtection':
      return t('Hide your landing pages from the competitors and spy tools.');
    case 'sharedReportsAmount':
      return t(
        'Create shared reports with the real-time data without granting the access to your account.',
      );
    case 'sendPostbacksCampaign':
      return t(
        'Use the option of hiding the initial amount of conversions for a campaign from your traffic source.',
      );
    case 'markersCampaigns':
    case 'markersCustomParams':
    case 'markersAffiliateNetworks':
    case 'markersTrafficSources':
    case 'markersFlows':
    case 'markersLandings':
    case 'markersOffers':
      return t(
        'Visualize your reports with marks - highlight the elements or custom parameters for further optimization.',
      );
    case 'trafficSourceParamMasking':
      return t(
        'Wish to hide the zones, websites and other traffic data from your affiliate networks?',
      );
    case 'integrationMgid':
    case 'integrationPropellerads':
    case 'integrationExoclick':
    case 'integrationTrafficjunky':
    case 'integrationRevcontent':
    case 'integrationFacebookConversions':
      return t(
        "Use API integrations to see more accurate data in your reports and automate the campaigns' management.",
      );
    case 'smartRotations':
      return t(
        'Use advanced rotations and fix-on option to distribute traffic smartly between paths, landing pages and offers.',
      );
    case 'dataRetention':
      return t('Wish to see the data for previous months?');
    case 'ivtIpIsDatacenter':
    case 'ivtIpIsBlacklisted':
    case 'ivtFastClicker':
    case 'ivtIpWithFrequentCampaignEvents':
    case 'ivtIpWithFrequentEvents':
      return t('Wish to identify and manage the invalid traffic in your campaigns?');
  }

  return getTitleNeedMore.value;
});

const getSubTitle = computed<string | undefined>(() => {
  if (isInvalidTrafficPack.value) {
    return `${t('Upgrade the subscription to enable the option or purchase it as add-on.')} <br> ${t('You should purchase all the features as add-ons or change the subscription to see the total amount of suspicious traffic.')}`;
  }

  switch (props.planLimits) {
    case 'domainsAmount':
    case 'redirectDomainsAmount':
    case 'campaignsAmount':
    case 'offersAmount':
    case 'landingsAmount':
    case 'flowsAmount':
    case 'trafficSourcesAmount':
    case 'affiliateNetworksAmount':
    case 'customConversionsAmount':
    case 'memberAdminsAmount':
    case 'memberUsersAmount':
    case 'dataRetention':
      return t('Upgrade the subscription to enable or purchase more as add-on.');
    case 'customColumnsAmount':
      return t('Upgrade the subscription to enable or purchase more columns as add-on.');
    case 'bulkEditCampaigns':
    case 'bulkEditOffers':
    case 'bulkEditLandings':
    case 'webViewCondition':
    case 'crawlerCondition':
    case 'proxyCondition':
    case 'referrerDomainCondition':
    case 'externalIdCondition':
    case 'botMarkCondition':
    case 'uniquenessPeriodCondition':
    case 'doubleMetaRedirect':
    case 'uniquenessPeriod':
    case 'sendPostbacksCampaign':
    case 'trafficLossCampaign':
    case 'landingPageProtection':
    case 'tagsCampaigns':
    case 'tagsOffers':
    case 'tagsLandings':
    case 'tagsFlows':
    case 'tagsTrafficSources':
    case 'trafficSourceParamMasking':
    case 'tagsAffiliateNetworks':
    case 'visitCapCondition':
    case 'integrationMgid':
    case 'integrationPropellerads':
    case 'integrationExoclick':
    case 'integrationTrafficjunky':
    case 'integrationRevcontent':
    case 'integrationFacebookConversions':
    case 'smartRotations':
    case 'bulkReportCustomParams':
      return t(
        'Upgrade the subscription to unlock the feature or purchase it as add-on.',
      );
    case 'importOffers':
      return t(
        'Upgrade the subscription to unlock the offers import or purchase it as add-on.',
      );
    case 'importLandings':
      return t(
        'Upgrade the subscription to unlock the landings import or purchase it as add-on.',
      );
    case 'bulkReportCampaign':
    case 'bulkReportOffer':
    case 'bulkReportLanding':
    case 'bulkReportFlow':
    case 'bulkReportTrafficSource':
    case 'bulkReportAffiliateNetwork':
      return t(
        'Upgrade the subscription to unlock the bulk reports or purchase it as add-on.',
      );
    case 'markersCampaigns':
    case 'markersOffers':
    case 'markersLandings':
    case 'markersFlows':
    case 'markersAffiliateNetworks':
    case 'markersTrafficSources':
    case 'markersCustomParams':
      return t('Upgrade the subscription to unlock the marks or purchase it as add-on.');
    case 'sharedReportsAmount':
      return t(
        'Upgrade the subscription to enable the option and create more reports or purchase it as add-on.',
      );
    case 'botFiltersAccess':
      return t(
        'Upgrade the subscription to set up the bot filters or purchase it as add-on.',
      );
    case 'ivtIpIsDatacenter':
    case 'ivtIpIsBlacklisted':
    case 'ivtFastClicker':
    case 'ivtIpWithFrequentCampaignEvents':
    case 'ivtIpWithFrequentEvents':
      return t('Upgrade the subscription to enable the option or purchase it as add-on.');
  }

  return getSubTitleNeedMore.value;
});

const enabledPlanLimit = computed(() => {
  const limits = plan.value?.limits;
  if (!limits) return [] as string[];
  return Object.keys(limits).filter(item => (limits as Record<string, unknown>)[item]);
});

const findNextPlanIndex = (planLimit: keyof BillingLimits) => {
  const allPlans = billingPlans.value;
  const currentLimit = billing.value.limits?.[planLimit];
  if (typeof currentLimit === 'number') {
    return allPlans.findIndex(
      item => +item.limits?.[planLimit] > +currentLimit,
    );
  }
  return allPlans.findIndex(item => Boolean(item.limits?.[planLimit]));
};

const planIndex = computed<number | null>(() => {
  if (billingPlans.value.length === 0) return null;

  if (typeof props.planLimits === 'string') {
    return findNextPlanIndex(props.planLimits);
  }

  const planIndexes: number[] = [];
  props.planLimits.addons.forEach(planLimit => {
    if (!billing.value.addons?.[planLimit]) {
      planIndexes.push(findNextPlanIndex(planLimit));
    }
  });

  if (props.planLimits.logic === PLAN_LIMITS_LOGIC.ALL_REQUIRED) {
    return planIndexes.reduce(
      (acc, currentValue) => (currentValue > acc ? currentValue : acc),
      planIndexes[0],
    );
  }
  if (props.planLimits.logic === PLAN_LIMITS_LOGIC.AT_LEAST_ONE_REQUIRED) {
    return planIndexes.reduce(
      (acc, currentValue) => (currentValue < acc ? currentValue : acc),
      planIndexes[0],
    );
  }

  return planIndexes[0] ?? null;
});

const plan = computed<BillingPlan | null>(() => {
  if (planIndex.value == null || planIndex.value < 0) return null;
  return (billingPlans.value[planIndex.value] ?? null) as BillingPlan | null;
});

const applySelectedPlan = (nextPlan: BillingPlan | null) => {
  if (!nextPlan?.id) return;
  props.handlePlanSelect?.(nextPlan);
  emit('select-plan', nextPlan);
};

// Match develop: select upgrade plan after mount (not during setup)
onMounted(() => {
  applySelectedPlan(plan.value);
});

watch(plan, (newPlan) => {
  applySelectedPlan(newPlan);
});

const byAsAddonHandle = (limit: keyof BillingLimits) => {
  emit('close');
  $emitter.emit('open-dialog', { name: 'ManageAddon', data: { planLimit: limit } });
};
</script>

<style lang="scss" scoped>
.wrp {
  display: flex;
  width: 100%;
  padding: 16px;
  align-items: flex-start;
}

.detailsWrp {
  flex: 1;
  padding-right: 16px;
}

.detailsTitle {
  font-size: 20px;
  line-height: 1.4;
  font-weight: bold;
  margin-bottom: 4px;
}

.subtitle {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 13px;
}

.greenBox {
  border-radius: 8px;
  background: var(--color-green10);
  padding: 8px 16px 14px 16px;
  display: flex;
  margin-bottom: 9px;
}

.greenBox__item {
  width: 33.333%;
  border-radius: 8px;
  background: var(--color-green10);
}

.greenBox__item-value {
  font-size: 20px;
  font-weight: bold;
}

.greenBox__item-unit {
  font-size: 20px;
  color: var(--color-gray40);
}

.greenBox__item-text {
  font-size: 12px;
}

.billedImmediately {
  font-size: 13px;
  margin-bottom: 15px;
}

.listLimitsTitle {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
}

.listLimits {
  column-count: 2;
  @media only screen and (max-width: 600px) {
    column-count: 1;
  }
}

.planDesktop {
}

.planMobile {
  display: none;
  margin-bottom: 16px;
}

@media only screen and (max-width: 600px) {
  .planMobile {
    display: block;
  }

  .planDesktop {
    display: none;
  }
}

.listLimits__item {
  display: flex;
  line-height: 1;
  align-items: start;
  margin-bottom: 8px;
}

.addons-list {
  display: flex;
  align-items: flex-start;
}

.buy-addon-btn {
  display: flex;
  flex-shrink: 0;
}

.more-than-one__wrp {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: 10px;
  & > div {
    width: calc(50% - 5px);
  }
}
</style>
